import React from 'react';

function CampaignMockup() {
  const contacts = [
    { name: 'Alex Rivera', status: 'Converted', color: 'text-green-400 bg-green-500/10 border-green-500/20' },
    { name: 'Jordan Lee', status: 'Answered', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
    { name: 'Sam Patel', status: 'Called', color: 'text-zinc-400 bg-white/5 border-white/10' },
    { name: 'Morgan Kim', status: 'Converted', color: 'text-green-400 bg-green-500/10 border-green-500/20' },
    { name: 'Taylor Wu', status: 'Calling…', color: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20' },
  ];

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-5 font-mono text-xs">
      <div className="flex items-center justify-between mb-3">
        <span className="text-zinc-400">Campaign: Q1 Lead Gen</span>
        <span className="text-green-400">● Running</span>
      </div>
      <div className="flex gap-4 mb-3 text-zinc-500">
        <span>1,200 contacts</span>
        <span>·</span>
        <span className="text-green-400">342 converted</span>
        <span>·</span>
        <span>28.5%</span>
      </div>
      <div className="border-t border-white/[0.06] pt-3 space-y-2">
        {contacts.map((c) => (
          <div key={c.name} className="flex items-center justify-between">
            <span className="text-zinc-300">{c.name}</span>
            <span className={`px-2 py-0.5 rounded-full border text-[10px] ${c.color}`}>{c.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResearchMockup() {
  const themes = ['Price sensitivity', 'Feature gaps', 'Competitor mentions', 'Onboarding friction'];
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-5 font-mono text-xs">
      <div className="text-zinc-400 mb-3">Voice Research Report · 48 interviews</div>
      <div className="flex gap-3 mb-3">
        {[
          { label: 'Positive', val: '62%', color: 'text-green-400' },
          { label: 'Neutral', val: '24%', color: 'text-zinc-400' },
          { label: 'Negative', val: '14%', color: 'text-red-400' },
        ].map((s) => (
          <div key={s.label} className="flex-1 rounded-lg border border-white/[0.06] bg-white/[0.03] p-2 text-center">
            <div className={`text-base font-bold ${s.color}`}>{s.val}</div>
            <div className="text-zinc-600 text-[10px]">{s.label}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {themes.map((t) => (
          <span key={t} className="px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-[10px]">{t}</span>
        ))}
      </div>
      <div className="border-t border-white/[0.06] pt-2 text-zinc-400 italic">
        "The pricing is actually really fair compared to what I expected."
      </div>
    </div>
  );
}

function PlannerMockup() {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-5 font-mono text-xs">
      <div className="text-zinc-400 mb-3">AI Planner · Intent: re-engage churned users</div>
      <div className="space-y-2 mb-3">
        {[
          { label: 'Audience', val: 'SMB SaaS, 11–50 employees, churned 30–90d' },
          { label: 'Tone', val: 'Empathetic, solution-focused' },
          { label: 'Timing', val: 'Tue–Thu, 10am–2pm local' },
        ].map((r) => (
          <div key={r.label} className="flex gap-2">
            <span className="text-zinc-600 shrink-0 w-16">{r.label}</span>
            <span className="text-zinc-300">{r.val}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-white/[0.06] pt-3">
        <div className="text-zinc-500 mb-1">Generated Script Preview</div>
        <div className="text-zinc-300 leading-relaxed">
          "Hi [Name], this is Jordan from Acme. I noticed you haven't logged in for a while and wanted to check if there was something we could have done better…"
        </div>
      </div>
    </div>
  );
}

const features = [
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
    label: 'AI Campaigns',
    headline: 'Run 1,000 calls while you sleep',
    body: 'Upload a contact list. Write your script or let AI write it. CallPilot dials, converses naturally, handles objections, and logs every outcome. Your team wakes up to a qualified pipeline.',
    visual: <CampaignMockup />,
    reverse: false,
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    label: 'Voice Research',
    headline: 'Market research that actually talks to people',
    body: 'Define what you want to learn. CallPilot calls your target audience, conducts real voice interviews, and delivers a report with key themes, sentiment data, and verbatim quotes — in hours, not weeks.',
    visual: <ResearchMockup />,
    reverse: true,
  },
  {
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
    label: 'AI Marketing Planner',
    headline: 'From idea to call script in 60 seconds',
    body: 'Describe your goal. CallPilot researches your market, profiles your ideal customer, and generates a complete campaign — messaging angles, call script, optimal timing, and projected conversion rates.',
    visual: <PlannerMockup />,
    reverse: false,
  },
];

export function Features() {
  return (
    <section id="features" className="py-24 px-6">
      <div className="mx-auto max-w-6xl">
        <div className="text-center mb-20">
          <span className="text-xs font-mono tracking-widest uppercase text-indigo-400 mb-3 block">Features</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            Your AI sales team, always on the phone
          </h2>
          <p className="mt-3 text-zinc-400 max-w-xl mx-auto">
            Three powerful modes. One platform. Works while your team sleeps.
          </p>
        </div>

        <div className="space-y-24">
          {features.map((f) => (
            <div
              key={f.label}
              className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center ${f.reverse ? 'md:[&>*:first-child]:order-2' : ''}`}
            >
              {/* Text */}
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-indigo-400 mb-4">
                  <span className="w-7 h-7 rounded-lg bg-indigo-500/15 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
                    {f.icon}
                  </span>
                  {f.label}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-4">{f.headline}</h3>
                <p className="text-zinc-400 leading-relaxed">{f.body}</p>
              </div>
              {/* Visual */}
              <div>{f.visual}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
